import { Autocomplete, Box, TextField } from "@mui/material";

const NULL_KEY = "__NULL__";

const makeEditComponent = ({
  choices,
  freeSolo = false,
  preferredNameKey = null,
}) => {
  const activeChoices = (choices ?? []).filter((c) => !c.deactivated);
  return (props) => {
    const { id, field, api } = props;

    const setValue = (value) => {
      api.setEditCellValue({ id, field, value });
      api.stopCellEditMode({ id, field });
    };

    const onChange = (_, selectedOption, reason) => {
      if (reason === "selectOption") {
        setValue(selectedOption?.key ?? null);
      } else if (reason === "createOption" && freeSolo) {
        setValue(selectedOption ?? null);
      }
    };

    const onClose = (_, reason) => {
      if (reason !== "toggleInput") {
        api.stopCellEditMode({ id, field });
      }
    };

    return (
      <Autocomplete
        options={activeChoices}
        getOptionKey={(option) =>
          freeSolo && typeof option === "string" ? option : option.key
        }
        getOptionLabel={(option) =>
          freeSolo && typeof option === "string"
            ? option
            : preferredNameKey
            ? option[preferredNameKey] ?? option.name
            : option.name
        }
        onChange={onChange}
        onClose={onClose}
        renderInput={(params) => (
          <Box
            sx={{
              padding: 0.5,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TextField
              {...params}
              variant={"outlined"}
              autoFocus
              size={"small"}
            />
          </Box>
        )}
        fullWidth
        open
        autoHighlight
        freeSolo={freeSolo}
      />
    );
  };
};

/**
 * @typedef {import("@mui/x-data-grid").GridColDef} GridColDef
 * @param {GridColDef & {freeSolo: boolean, translations: object}} colProps
 */
export const makeChoicesColDef = ({
  field,
  choices,
  editable = true,
  sortable = false,
  freeSolo = false,
  translations = null,
  searchNameKey = null,
  ...colProps
}) => {
  const keyToNameMapping = Object.fromEntries(
    (choices ?? []).map((c) => [c.key, c.name])
  );
  return {
    field,
    type: "string",
    sortable,
    editable,
    valueGetter: (value) => value ?? NULL_KEY,
    valueSetter: (value, row) => {
      if (freeSolo || keyToNameMapping[value] != null) {
        return {
          ...row,
          [field]: value === NULL_KEY ? null : value,
        };
      } else {
        return row;
      }
    },
    valueFormatter: (value) =>
      ({
        [NULL_KEY]: "-- Please Select --",
        ...keyToNameMapping,
        ...translations,
      }[value] ?? (freeSolo ? value : "<Unknown>")),
    renderEditCell: makeEditComponent({
      choices,
      freeSolo,
      preferredNameKey: searchNameKey,
    }),
    ...colProps,
  };
};
